import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationOnIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  DateRange as DateRangeIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import api from '../../../api/api';
import InventoryHistoryEvents from './InventoryHistoryEvents';

const AssetHistoryPage = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  
  const [historyData, setHistoryData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [assetInfo, setAssetInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState('summary');
  
  // Состояния для редактирования
  const [editMode, setEditMode] = useState(false);
  const requestVersion = useRef(0);

  useEffect(() => {
    setEditMode(false);
    if (assetId) {
      fetchAllData();
    } else { setLoading(false); }
    return () => { requestVersion.current += 1; };
  }, [assetId]);

  const fetchAllData = async () => {
    const version = ++requestVersion.current;
    try {
      setLoading(true);
      setError(null);

      const [historyResponse, statsResponse, assetResponse] = await Promise.all([
        api.get(`/assets/${assetId}/rental-history`),
        api.get(`/assets/${assetId}/rental-stats`).catch(() => ({ data: null })),
        api.get(`/assets/${assetId}`).catch(() => ({ data: null }))
      ]);

      if (version !== requestVersion.current) return;
      setHistoryData(historyResponse.data);
      setStatsData(statsResponse.data);
      setAssetInfo(assetResponse.data);
    } catch (err) {
      console.error('Ошибка загрузки истории аренды актива:', err);
      if (version !== requestVersion.current) return;
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Ошибка загрузки данных');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
    } catch {
      return 'Неверная дата';
    }
  };

  const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'активна':
      case 'active':
        return 'primary';
      case 'возвращен':
      case 'completed':
        return 'success';
      case 'просрочен':
      case 'overdue':
        return 'error';
      case 'отменен':
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'активна':
      case 'active':
        return <AccessTimeIcon fontSize="small" />;
      case 'возвращен':
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'просрочен':
      case 'overdue':
        return <WarningIcon fontSize="small" />;
      case 'отменен':
      case 'cancelled':
        return <ErrorIcon fontSize="small" />;
      default:
        return <AssignmentIcon fontSize="small" />;
    }
  };

  const handleEditDeal = (deal) => {
    navigate(`/rental-deals/edit/${deal.deal_id}`);
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Вы уверены, что хотите открепить этот актив от сделки? Это действие нельзя отменить.')) {
      return;
    }
    
    try {
      await api.delete(`/rental-asset-deals/asset/${assetId}/deal/${dealId}`);
      // Обновляем данные после удаления
      await fetchAllData();
      alert('Актив успешно откреплен от сделки');
    } catch (error) {
      console.error('Ошибка при откреплении актива:', error);
      alert('Ошибка при откреплении актива: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <IconButton aria-label="Повторить загрузку" color="inherit" size="small" onClick={fetchAllData}>
            <RefreshIcon />
          </IconButton>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!historyData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          История аренды актива не найдена
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Хлебные крошки */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(event) => { event.preventDefault(); navigate('/'); }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <DashboardIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Главная
        </Link>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(event) => { event.preventDefault(); navigate('/assets'); }}
        >
          Активы
        </Link>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(event) => { event.preventDefault(); navigate(`/assets/edit/${assetId}`); }}
        >
          {assetInfo?.name || `Актив #${assetId}`}
        </Link>
        <Typography color="text.primary">История аренды</Typography>
      </Breadcrumbs>

      {/* Заголовок страницы */}
      <Box className="erp-section-heading" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton aria-label="Вернуться к активу" onClick={() => navigate(`/assets/edit/${assetId}`)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <HistoryIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">
              История аренды
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {assetInfo?.name || `Актив #${assetId}`}
            </Typography>
          </Box>
        </Box>
        
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAllData}
            sx={{ mr: 2 }}
          >
            Обновить
          </Button>
          <Button
            variant={editMode ? "contained" : "outlined"}
            startIcon={editMode ? <SaveIcon /> : <EditIcon />}
            onClick={() => setEditMode(!editMode)}
            color={editMode ? "success" : "primary"}
          >
            {editMode ? "Выйти из режима редактирования" : "Режим редактирования"}
          </Button>
        </Box>
      </Box>

      {/* Краткая сводка */}
      <Accordion expanded={expanded === 'summary'} onChange={handleAccordionChange('summary')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6">
              Краткая сводка
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary">
                    {historyData.summary.total_rentals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Всего аренд
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="success.main">
                    {historyData.summary.active_rentals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Активных
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="text.primary">
                    {historyData.summary.completed_rentals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Завершенных
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="error.main">
                    {historyData.summary.damaged_returns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    С повреждениями
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(historyData.summary.total_revenue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Общая выручка
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Статистика использования */}
      {statsData && (
        <Accordion expanded={expanded === 'stats'} onChange={handleAccordionChange('stats')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUpIcon color="info" />
              <Typography variant="h6">
                Статистика использования
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Эффективность
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Загруженность:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {number(statsData.utilization_rate).toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(statsData.utilization_rate, 100)} 
                          color={statsData.utilization_rate > 70 ? 'success' : statsData.utilization_rate > 30 ? 'warning' : 'error'}
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Дней в аренде:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {statsData.total_days_rented} из {statsData.total_days_available}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Выручка за день:</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {formatCurrency(statsData.revenue_per_day)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Средняя длительность:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {number(statsData.average_rental_duration).toFixed(1)} дней
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Качество обслуживания
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Возвраты вовремя:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {number(statsData.on_time_return_rate).toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={statsData.on_time_return_rate} 
                          color="success"
                        />
                      </Box>
                      <Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Повреждения:</Typography>
                          <Typography variant="body2" fontWeight="bold" color={statsData.damage_rate > 10 ? 'error.main' : 'success.main'}>
                            {number(statsData.damage_rate).toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(statsData.damage_rate, 100)} 
                          color={statsData.damage_rate > 10 ? 'error' : 'success'}
                        />
                      </Box>
                      {statsData.most_frequent_client && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Частый клиент:
                          </Typography>
                          <Chip 
                            icon={<PersonIcon />}
                            label={statsData.most_frequent_client} 
                            color="primary" 
                            variant="outlined"
                          />
                        </Box>
                      )}
                      {(statsData.peak_season_months || []).length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Пиковые месяцы:
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {(statsData.peak_season_months || []).map((month, index) => (
                              <Chip
                                key={index}
                                icon={<DateRangeIcon />}
                                label={month}
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* История сделок */}
      <Accordion expanded={expanded === 'deals'} onChange={handleAccordionChange('deals')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1} justifyContent="space-between" width="100%">
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon color="action" />
              <Typography variant="h6">
                История аренд ({historyData.deals.length})
              </Typography>
            </Box>
            {editMode && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/rental-deals/edit/new");
                }}
              >
                Добавить сделку
              </Button>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Клиент</TableCell>
                  <TableCell>Сделка</TableCell>
                  <TableCell>Период</TableCell>
                  <TableCell>Длительность</TableCell>
                  <TableCell>Сумма</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Состояние</TableCell>
                  {editMode && <TableCell>Действия</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData.deals.map((deal) => (
                  <TableRow key={`${deal.deal_id}-${deal.client?.client_id}`}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {deal.client?.client_name || 'Клиент не указан'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {deal.client?.contact_info}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{deal.deal_number}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDate(deal.start_date)}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(deal.end_date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {deal.duration_days ? `${deal.duration_days} дн.` : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {deal.history_only ? '—' : formatCurrency(deal.total_amount)}
                      </Typography>
                      {/* Убрана строка с ценой за период - оставлена только итоговая стоимость */}
                      {/* {deal.rental_price_per_period && deal.tariff_period && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatCurrency(deal.rental_price_per_period)}/{deal.tariff_period}
                        </Typography>
                      )} */}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(deal.status)}
                        label={deal.status}
                        color={getStatusColor(deal.status)}
                        size="small"
                      />
                      <InventoryHistoryEvents events={deal.inventory_events} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {deal.is_damaged && (
                          <Chip
                            icon={<BuildIcon />}
                            label="Повреждение"
                            color="error"
                            size="small"
                          />
                        )}
                        {deal.is_overdue && (
                          <Chip
                            icon={<WarningIcon />}
                            label="Просрочка"
                            color="warning"
                            size="small"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    {editMode && (
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Редактировать">
                            <IconButton size="small" onClick={() => handleEditDeal(deal)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!deal.history_only && <Tooltip title="Удалить">
                            <IconButton size="small" color="error" onClick={() => handleDeleteDeal(deal.deal_id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>


    </Box>
  );
};

export default AssetHistoryPage;
